import React, {useState} from 'react';

import {Icon, IconButton, Text} from '@albert-io/atomic';
import type {IconProp, IconStyle} from '@albert-io/atomic/atoms/Icon/Icon.react';
import {history} from 'client/history';

import {formatDateRange, formatDateTime, getDueCopy} from 'client/TeacherDashboard/rowDateCopy';

import type {SkillPathRowData} from './types';

const completedCopy = (row: SkillPathRowData): string =>
  `${row.completedStudents} / ${row.totalStudents} completed`;

const getSubtitleSections = (row: SkillPathRowData, showExactDueDate: boolean): string[] => {
  if (row.status === 'scheduled') {
    return [
      row.minStartDate
        ? `Starts ${formatDateRange(row.minStartDate, row.maxStartDate, formatDateTime)}`
        : 'Scheduled'
    ];
  }
  const parts = [getDueCopy(row, showExactDueDate), completedCopy(row)];
  return row.status === 'closed' ? ['Closed', ...parts] : parts;
};

const STATUS_DISPLAY: Record<
  SkillPathRowData['status'],
  {color: 'tertiary' | 'warning'; icon?: IconProp; iconStyle?: IconStyle}
> = {
  open: {color: 'tertiary'},
  scheduled: {color: 'warning', icon: 'clock', iconStyle: 'light'},
  closed: {color: 'tertiary', icon: 'ban', iconStyle: 'regular'}
};

interface Props {
  row: SkillPathRowData;
  reportPath: string | null;
  onEdit: (row: SkillPathRowData) => void;
}

const SkillPathRowBase = ({row, reportPath, onEdit}: Props) => {
  const [showExactDueDate, setShowExactDueDate] = useState(false);
  const subtitleSections = getSubtitleSections(row, showExactDueDate);
  const {
    color: statusColor,
    icon: statusIcon,
    iconStyle: statusIconStyle
  } = STATUS_DISPLAY[row.status];

  return (
    <div
      className='gap-x2 rounded-x1 p-x1 hover:background-primary-hover flex cursor-pointer items-center'
      data-testid='skill-path-row'
      onMouseEnter={() => setShowExactDueDate(true)}
      onMouseLeave={() => setShowExactDueDate(false)}
      onFocus={() => setShowExactDueDate(true)}
      onBlur={() => setShowExactDueDate(false)}
    >
      <span className='h-x5 w-x5 background-special-subtle content-special flex shrink-0 items-center justify-center rounded-full'>
        <Icon color='inherit' icon='sparkles' iconStyle='light' />
      </span>
      <div className='min-w-x0 flex grow flex-col'>
        <Text as='div' className='content-primary truncate' size='m'>
          {row.skillName}
        </Text>
        <Text as='div' className='truncate' color={statusColor} size='s'>
          {statusIcon ? (
            <Icon
              className='mr-x0-half text-xs!'
              color={statusColor}
              icon={statusIcon}
              iconStyle={statusIconStyle}
            />
          ) : null}
          {subtitleSections.map((part, index) => (
            <React.Fragment key={part}>
              {index > 0 && <span className='mx-x1'>•</span>}
              {part}
            </React.Fragment>
          ))}
        </Text>
      </div>
      <div className='gap-x0-half flex shrink-0 items-center'>
        {reportPath && (
          <IconButton
            icon='chart-line'
            iconStyle='regular'
            label='View Report'
            tooltip='View Report'
            size='s'
            onClick={() => history.pushState(null, reportPath)}
          />
        )}
        <IconButton
          icon='pencil'
          iconStyle='regular'
          label='Edit Skill Path'
          tooltip='Edit Skill Path'
          size='s'
          onClick={() => onEdit(row)}
        />
      </div>
    </div>
  );
};

export const SkillPathRow = React.memo(SkillPathRowBase);
